// Decision 44: an invoice is overdue when it is still payable (status 'sent' OR 'overdue') AND its
// due date has passed. Mirrors the backend (lib/invoiceStatus.js PAYABLE_STATUSES and
// lib/followUpEngine.js RULE 3: dueDate < today). Checking only status === 'overdue' missed every
// 'sent' invoice past its due date, so the Financial overview said "Nothing overdue" while money
// was late.

export const PAYABLE_STATUSES = ['sent', 'overdue'];

// Today as YYYY-MM-DD in the viewer's own timezone. toISOString() is UTC, which in British
// Summer Time gives yesterday's date for the first hour after midnight.
export function localToday(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function isInvoicePayable(inv) {
  return !!inv && PAYABLE_STATUSES.includes(inv.status);
}

// Due today is not overdue; a payable invoice with no due date never is.
// Block H-5 (Carla's decision 4): the client paid; some or all of it may have been given back.
// Mirrors Aidey Backend/lib/invoiceStatus.js. A screen that asked status === 'paid' asks this.
export const PAID_STATUSES = ['paid', 'partially_refunded', 'refunded'];
export const isPaidStatus = (status) => PAID_STATUSES.includes(status);
// What the business kept of an invoice's total: the total less refunds, 0 if it was never paid.
export function keptTotal(inv, total) {
  if (!inv || !isPaidStatus(inv.status)) return 0;
  return Math.max(0, Number(total || 0) - Number(inv.refundedAmount || 0));
}

export function isInvoiceOverdue(inv, today = localToday()) {
  if (!isInvoicePayable(inv)) return false;
  // Block H-5: the SERVER's answer when it sent one (end of the due day, owner's zone; every owner
  // response carries it). The browser's date check is only for an invoice that has none.
  if (typeof inv.overdue === 'boolean') return inv.overdue;
  if (!inv.dueDate) return false;
  return String(inv.dueDate).slice(0, 10) < today;
}

// Block H-5 (Carla's live check, 2026-10-10): what ONE invoice is called on the owner's screens.
// The row, the tabs, the tiles and the header all ask this, so they cannot disagree again (the row
// said "Pending" for an overdue invoice because 'sent' was missing from its map).
//   draft | paid | partially_refunded | refunded | cancelled | overdue | pending (sent, not yet due)
export function invoiceDisplayStatus(inv) {
  if (!inv) return 'pending';
  if (['draft', 'paid', 'partially_refunded', 'refunded', 'cancelled'].includes(inv.status)) return inv.status;
  return isInvoiceOverdue(inv) ? 'overdue' : 'pending';
}

// Fresh-eyes item 24 (Carla, 2026-10-12): the word a person reads for each displayed status. Sent and
// not yet due is "Due", as the client's portal and the public invoice say (it read "Pending" on the
// owner's screens). The internal key stays 'pending' and stored statuses do not change.
export const INVOICE_STATUS_WORD = {
  draft: 'Draft', pending: 'Due', overdue: 'Overdue', paid: 'Paid', cancelled: 'Cancelled',
  partially_refunded: 'Partially refunded', refunded: 'Refunded',
};
export function invoiceStatusWord(inv) {
  return INVOICE_STATUS_WORD[invoiceDisplayStatus(inv)];
}
